// "Selling the phone you're holding?" The browser can tell us roughly what it runs on: Android browsers report the
// model (Chrome through User-Agent Client Hints, others in the user agent), iPhones only say "iPhone".

export type Detected = { brand: string; model: string | null };

// Samsung model codes (region letters dropped), for the models in the device library. Unknown codes still give the brand.
const SAMSUNG: [RegExp, string][] = [
  [/^SM-S938/, "Galaxy S25 Ultra"],
  [/^SM-S937/, "Galaxy S25 Edge"],
  [/^SM-S931/, "Galaxy S25"],
  [/^SM-S731/, "Galaxy S25 FE"],
  [/^SM-S928/, "Galaxy S24 Ultra"],
  [/^SM-S921/, "Galaxy S24"],
  [/^SM-S918/, "Galaxy S23 Ultra"],
  [/^SM-S911/, "Galaxy S23"],
  [/^SM-G991/, "Galaxy S21"],
  [/^SM-F966/, "Galaxy Z Fold7"],
  [/^SM-F766/, "Galaxy Z Flip7"],
  [/^SM-F946/, "Galaxy Z Fold5"],
  [/^SM-F731/, "Galaxy Z Flip5"],
  [/^SM-A556/, "Galaxy A55"],
  [/^SM-A356/, "Galaxy A35"],
];

function fromModel(model: string): Detected | null {
  const m = model.trim();
  if (!m || m === "K") return null; // Chrome's reduced user agent hides the model as "K"
  if (/^SM-/i.test(m)) return { brand: "Samsung", model: SAMSUNG.find(([re]) => re.test(m.toUpperCase()))?.[1] ?? null };
  if (/^Pixel\b/i.test(m)) return { brand: "Google", model: m.replace(/^pixel/i, "Pixel") };
  if (/redmi|xiaomi|\bmi\b|poco/i.test(m)) return { brand: "Xiaomi", model: null };
  if (/oneplus/i.test(m)) return { brand: "OnePlus", model: null };
  if (/honor/i.test(m)) return { brand: "Honor", model: null };
  if (/huawei/i.test(m)) return { brand: "Huawei", model: null };
  return null;
}

type UAData = { platform?: string; getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }> };

export async function detectDevice(): Promise<Detected | null> {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return { brand: "Apple", model: null };
  if (!/Android/.test(ua)) return null;
  const data = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  if (data?.getHighEntropyValues) {
    try {
      const { model } = await data.getHighEntropyValues(["model"]);
      const hit = model && fromModel(model);
      if (hit) return hit;
    } catch {
      // Not allowed or not supported: fall back to the user agent string.
    }
  }
  const m = ua.match(/Android [\d.]+; ([^;)]+?)(?: Build\/|\))/);
  return m ? fromModel(m[1]) : null;
}
